import { useState, useEffect, useCallback } from "react";
import logger from "../utils/logger";
import { useNavigate, useLocation } from "react-router-dom";
import { useAffAuth, AFF_API, getAffHeaders } from "../context/AffAuthContext";
import axios from "axios";
import { toast } from "sonner";
import PartnerHome from "../components/affiliate/PartnerHome";
import HomeHero from "../components/affiliate/HomeHero";
import IndianMarketPage from "../components/affiliate/IndianMarketPage";
import Aff2FASetup from "../components/affiliate/Aff2FASetup";
import Leaderboard from "../components/affiliate/Leaderboard";
import ProfilePage from "../components/affiliate/ProfilePage";
import TransactionsPage from "../components/affiliate/TransactionsPage";
import NotificationBell from "../components/affiliate/NotificationBell";
import MT5Accounts from "../components/affiliate/MT5Accounts";
import TradingViewIndicator from "../components/affiliate/TradingViewIndicator";
import { useTheme } from "../context/ThemeContext";
import { SignOut, House, Trophy, UserCircle, ArrowsLeftRight, CaretDown, Users, ChartLine, Sun, Moon, Flag } from "@phosphor-icons/react";

export default function AffDashboard() {
  const { user, loading, logout } = useAffAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = (new URLSearchParams(location.search).get("tab") || "home").toLowerCase();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [activeView, setActiveView] = useState(initialTab);
  const [profileOpen, setProfileOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${AFF_API}/affiliate/dashboard`, { headers: getAffHeaders() });
      setData(res.data);
    } catch { logger.error("Dashboard fetch error"); }
    finally { setFetching(false); }
  }, []);

  useEffect(() => { if (!loading && !user) navigate("/affiliate/login", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  if (loading || fetching) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--velox-bg)" }}>
      <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "var(--velox-accent-cyan)", borderTopColor: "transparent" }} />
    </div>
  );
  if (!user || !data) return null;

  const isLight = theme === "light";

  return (
    <div className="min-h-screen" style={{ background: "var(--velox-bg)", color: "var(--velox-text)" }} data-testid="aff-dashboard">
      <header
        className="sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between"
        style={{
          background: isLight ? "rgba(255,255,255,0.85)" : "rgba(11,19,43,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid var(--velox-border)",
        }}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "var(--velox-accent-cyan)15", border: "1px solid var(--velox-border)" }}>
              <img src="https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png" alt="VeloxAlgoTech LLP" className="w-9 h-9" />
            </div>
            <span
              className="text-xs lg:text-sm font-extrabold tracking-[0.08em] leading-[1.1] hidden sm:flex flex-col"
              style={{ fontFamily: "Manrope" }}
              data-testid="brand-name-aff-dashboard"
            >
              <span style={{ color: 'var(--velox-accent-cyan)' }}>Velox</span>
              <span style={{ color: 'var(--velox-text)' }}>AlgoTech</span>
            </span>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {[
              { id: "home", label: "Dashboard", icon: House, always: true },
              { id: "trading", label: "Trading", icon: ChartLine, always: true },
              { id: "indian-market", label: "Indian Market", icon: Flag, indianOnly: true },
              { id: "affiliate", label: "Affiliate", icon: Users, indianOnly: true },
              { id: "transactions", label: "Transactions", icon: ArrowsLeftRight, indianOnly: true },
              { id: "leaderboard", label: "Leaderboard", icon: Trophy, indianOnly: true },
            ]
              .filter((tab) => !tab.indianOnly || !data.mlm_disabled)
              .map((tab) => {
              const Icon = tab.icon;
              const active = activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveView(tab.id); setProfileOpen(false); }}
                  data-testid={`aff-nav-${tab.id}`}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5"
                  style={{
                    background: active ? "var(--velox-panel)" : "transparent",
                    color: active ? "var(--velox-accent-cyan)" : "var(--velox-text-muted)",
                    border: `1px solid ${active ? "var(--velox-border-strong)" : "transparent"}`,
                  }}
                >
                  <Icon size={14} /> <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Theme toggle (Sun / Moon) */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
            className="w-8 h-8 rounded-md flex items-center justify-center transition-all"
            style={{
              background: "var(--velox-panel)",
              border: "1px solid var(--velox-border)",
              color: isLight ? "var(--velox-accent-blue)" : "var(--velox-accent-cyan)",
            }}
          >
            {isLight ? <Moon size={14} weight="duotone" /> : <Sun size={14} weight="duotone" />}
          </button>

          <NotificationBell />

          {/* Profile dropdown */}
          <div className="relative">
            <button onClick={() => { setProfileOpen(!profileOpen); }}
              data-testid="profile-dropdown-btn"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md transition-all"
              style={{
                background: (profileOpen || activeView === "profile") ? "var(--velox-panel)" : "transparent",
              }}>
              {data.picture ? <img src={data.picture} alt="" className="w-7 h-7 rounded-full" style={{ border: "1px solid var(--velox-border)" }} /> :
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--velox-panel)" }}><UserCircle size={16} style={{ color: "var(--velox-text-muted)" }} /></div>}
              <span className="text-sm hidden sm:block max-w-[100px] truncate" style={{ color: "var(--velox-text)" }}>{data.name}</span>
              <CaretDown size={10} style={{ color: "var(--velox-text-muted)" }} />
            </button>

            {profileOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow-xl z-50 py-1"
                  style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }}
                  data-testid="profile-dropdown-menu"
                >
                  <button onClick={() => { setActiveView("profile"); setProfileOpen(false); }}
                    data-testid="profile-menu-account"
                    className="w-full px-4 py-2.5 text-left text-xs transition-colors flex items-center gap-2 hover:bg-white/5"
                    style={{ color: "var(--velox-text-muted)" }}>
                    <UserCircle size={14} /> Account Details
                  </button>
                  <button onClick={() => { setActiveView("transactions"); setProfileOpen(false); }}
                    data-testid="profile-menu-transactions"
                    className="w-full px-4 py-2.5 text-left text-xs transition-colors flex items-center gap-2 hover:bg-white/5"
                    style={{ color: "var(--velox-text-muted)" }}>
                    <ArrowsLeftRight size={14} /> Transactions
                  </button>
                  <div style={{ borderTop: "1px solid var(--velox-border)", margin: "4px 0" }} />
                  <button onClick={logout} data-testid="profile-menu-logout"
                    className="w-full px-4 py-2.5 text-left text-xs text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center gap-2">
                    <SignOut size={14} /> Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === "home" && !data.mlm_disabled && (
          <div className="space-y-6">
            <TradingViewIndicator />
            <HomeHero
              profile={data}
              onNavigate={(view) => { setActiveView(view); setProfileOpen(false); }}
            />
          </div>
        )}
        {activeView === "home" && data.mlm_disabled && (
          <div className="space-y-8" data-testid="non-indian-home">
            <TradingViewIndicator />
            {/* Welcome banner */}
            <div
              className="rounded-2xl p-6 md:p-8"
              style={{
                background: "linear-gradient(135deg, rgba(0,180,216,0.10) 0%, rgba(6,214,160,0.05) 100%)",
                border: "1px solid var(--velox-border)",
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🌍</span>
                <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: "var(--velox-text)", fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Welcome, {data.name || "Trader"}
                </h1>
              </div>
              <p className="text-sm" style={{ color: "var(--velox-text-muted)" }}>
                You&apos;re registered as a <span className="font-bold">Non-Indian Trader</span> from
                <span className="font-bold"> {data.country || "your country"}</span>.
                Trading and MT5 subscriptions are fully unlocked · affiliate features are
                <span className="font-bold text-amber-300"> coming soon</span>.
              </p>
              <button
                onClick={() => setActiveView("trading")}
                data-testid="non-indian-goto-trading"
                className="mt-5 px-4 py-2 rounded-md text-xs font-bold transition-all"
                style={{ background: "var(--velox-accent-cyan)", color: "#0b132b" }}
              >
                Go to Trading →
              </button>
            </div>

            {/* About VeloxAlgoTech LLP */}
            <section className="rounded-2xl p-6 md:p-10 text-center" style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }} data-testid="ni-about">
              <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4" style={{ color: "var(--velox-text)", fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Welcome to <span style={{ color: "var(--velox-accent-cyan)" }}>VeloxAlgoTech LLP</span>
              </h2>
              <p className="max-w-3xl mx-auto text-sm md:text-base leading-relaxed" style={{ color: "var(--velox-text-muted)" }}>
                VeloxAlgoTech LLP is a premier fintech company dedicated to providing high-performance, high-timeframe market analysis.
                Our systems empower traders with precise technical insights to navigate global markets seamlessly.
              </p>
            </section>

            {/* THE ANATOMY OF A MATRIX — TRADER mindset */}
            <section data-testid="ni-trader-anatomy">
              <div className="text-center mb-8">
                <p className="text-[10px] font-bold tracking-[0.4em] mb-3" style={{ color: "var(--velox-accent-cyan)", fontFamily: "'IBM Plex Mono', monospace" }}>
                  THE ANATOMY OF A MATRIX
                </p>
                <h3 className="text-2xl md:text-4xl font-black tracking-tight" style={{ color: "var(--velox-text)", fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  T.R.A.D.E.R
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { letter: "T", label: "TIME",       quote: "Your time is limited, so don't waste it living someone else's life.", who: "STEVE JOBS" },
                  { letter: "R", label: "RISK",       quote: "The biggest risk in life is not taking any risk.",                   who: "MARK ZUCKERBERG" },
                  { letter: "A", label: "ANALYSIS",   quote: "Price is what you pay. Value is what you get.",                      who: "WARREN BUFFETT" },
                  { letter: "D", label: "DISCIPLINE", quote: "The big money is not in the buying and selling, but in the waiting.", who: "CHARLIE MUNGER" },
                  { letter: "E", label: "EMOTION",    quote: "If investing is entertaining for you, if it's fun, you're probably not making any money.", who: "GEORGE SOROS" },
                  { letter: "R", label: "REWARDS",    quote: "Pain + Reflection = Progress.",                                       who: "RAY DALIO" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-xl transition-all hover:-translate-y-0.5"
                    style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }}
                    data-testid={`ni-trader-${item.label.toLowerCase()}`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-2xl font-black"
                        style={{
                          background: "linear-gradient(135deg, rgba(0,229,255,0.15), rgba(6,214,160,0.10))",
                          border: "1px solid var(--velox-accent-cyan)",
                          color: "var(--velox-accent-cyan)",
                          fontFamily: "'Cabinet Grotesk', sans-serif",
                        }}
                      >
                        {item.letter}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold tracking-[0.28em] mb-2" style={{ color: "var(--velox-accent-cyan)", fontFamily: "'IBM Plex Mono', monospace" }}>
                          {item.label}
                        </p>
                        <p className="text-sm italic leading-relaxed mb-2" style={{ color: "var(--velox-text)" }}>
                          &ldquo;{item.quote}&rdquo;
                        </p>
                        <p className="text-[10px] font-bold tracking-[0.14em]" style={{ color: "var(--velox-text-muted)" }}>
                          — {item.who}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Closing mindset tagline */}
            <section
              className="rounded-2xl p-8 md:p-12 text-center"
              style={{
                background: "linear-gradient(135deg, rgba(0,180,216,0.08), rgba(6,214,160,0.04))",
                border: "1px solid var(--velox-accent-cyan)",
              }}
              data-testid="ni-mindset-tagline"
            >
              <p className="text-xl md:text-3xl font-black tracking-tight" style={{ color: "var(--velox-text)", fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                TRADER: <span style={{ color: "var(--velox-accent-cyan)" }}>NOT JUST A PROFESSION.</span> IT&apos;S A MINDSET.
              </p>
            </section>
          </div>
        )}
        {activeView === "trading" && (
          <div className="space-y-6">
            <TradingViewIndicator />
            <MT5Accounts profile={data} onUpdate={fetchData} />
            <Aff2FASetup enabled={data.two_fa_enabled} onUpdate={fetchData} />
          </div>
        )}
        {activeView === "indian-market" && !data.mlm_disabled && <IndianMarketPage />}
        {activeView === "affiliate" && !data.mlm_disabled && (
          <PartnerHome profile={data} />
        )}
        {activeView === "transactions" && !data.mlm_disabled && <TransactionsPage wallet={data.wallet} onUpdate={fetchData} />}
        {activeView === "leaderboard" && !data.mlm_disabled && <Leaderboard />}
        {data.mlm_disabled && ["indian-market", "affiliate", "leaderboard", "transactions"].includes(activeView) && (
          <div className="flex flex-col items-center justify-center py-24 px-6 text-center" data-testid="aff-mlm-coming-soon">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: "var(--velox-panel)", border: "1px solid var(--velox-border)" }}>
              <Users size={26} weight="duotone" style={{ color: "var(--velox-accent-cyan)" }} />
            </div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight mb-2" style={{ color: "var(--velox-text)", fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Affiliate program · Coming soon
            </h2>
            <p className="text-sm max-w-md" style={{ color: "var(--velox-text-muted)" }}>
              The VeloxAlgoTech LLP affiliate & referral program is currently available to Indian citizens only.
              We&apos;re rolling it out globally shortly — you&apos;ll be notified by email when it goes live for
              <span className="font-bold"> {data.country || "your region"}</span>.
            </p>
            <button
              onClick={() => setActiveView("trading")}
              data-testid="aff-back-to-trading"
              className="mt-6 px-4 py-2 rounded-md text-xs font-bold transition-all"
              style={{ background: "var(--velox-accent-cyan)", color: "#0b132b" }}
            >
              Back to Trading
            </button>
          </div>
        )}
        {activeView === "profile" && <ProfilePage data={data} onUpdate={fetchData} />}
      </main>
    </div>
  );
}
